import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  Upload, ChevronRight, ChevronLeft, Check,
  AlertTriangle, X, ArrowLeft, CheckCircle, XCircle
} from 'lucide-react'
import Papa from 'papaparse'
import api, { getAllCattle } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'

// =============================================================================
// STEP INDICATOR
// =============================================================================

function StepIndicator({ currentStep }) {
  const steps = ['Upload CSV', 'Preview & Match', 'Results']
  return (
    <div className="flex items-center justify-center mb-8">
      {steps.map((label, i) => (
        <div key={label} className="flex items-center">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
            i < currentStep
              ? 'bg-green-100 text-green-700'
              : i === currentStep
                ? 'bg-primary-100 text-primary-700'
                : 'bg-gray-100 text-gray-500'
          }`}>
            {i < currentStep ? (
              <Check className="h-4 w-4" />
            ) : (
              <span className="h-5 w-5 rounded-full bg-current/20 flex items-center justify-center text-xs">
                {i + 1}
              </span>
            )}
            {label}
          </div>
          {i < steps.length - 1 && (
            <ChevronRight className="h-5 w-5 mx-2 text-gray-400" />
          )}
        </div>
      ))}
    </div>
  )
}

// =============================================================================
// SESSION TIME TOGGLE
// =============================================================================

function SessionTimeToggle({ value, onChange }) {
  const options = [
    { value: 'AM', label: 'AM' },
    { value: 'PM', label: 'PM' },
    { value: 'Evening', label: 'Evening' },
  ]
  return (
    <div className="flex rounded-lg border border-gray-300 overflow-hidden">
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
            value === opt.value
              ? 'bg-primary-600 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

// =============================================================================
// FLEXIBLE CSV HEADER MATCHING
// =============================================================================

function resolveHeaders(headers) {
  const lower = headers.map(h => h.toLowerCase().trim())
  const find = (...candidates) => {
    for (const c of candidates) {
      const idx = lower.findIndex(h => h === c || h.includes(c))
      if (idx >= 0) return headers[idx]
    }
    return null
  }
  return {
    rfid: find('rfid', 'tag', 'eartag', 'eid', 'electronic id', 'e.i.d'),
    volume: find('volume', 'liters', 'litres', 'milk', 'yield'),
    duration: find('duration', 'minutes', 'time', 'milking_time'),
    order: find('order', 'milking_order', 'sequence', 'seq'),
  }
}

// Column mapping options for manual override
const COLUMN_ROLES = [
  { value: '', label: '-- Skip --' },
  { value: 'rfid', label: 'RFID / Tag' },
  { value: 'volume', label: 'Volume (liters)' },
  { value: 'duration', label: 'Duration (minutes)' },
  { value: 'order', label: 'Milking Order' },
]

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function MilkSessionImport({ ranchId }) {
  const navigate = useNavigate()
  const { userId } = useAuth()
  const [step, setStep] = useState(0)
  const [error, setError] = useState('')

  // Step 0 state
  const [sessionDate, setSessionDate] = useState(new Date().toISOString().split('T')[0])
  const [sessionTime, setSessionTime] = useState('AM')
  const [milkingMethod, setMilkingMethod] = useState('hand')
  const [fileName, setFileName] = useState('')
  const [rawHeaders, setRawHeaders] = useState([])
  const [parsedRows, setParsedRows] = useState([])
  const [parseErrors, setParseErrors] = useState([])
  const [headerMapping, setHeaderMapping] = useState({})  // { originalHeader: role }
  const [autoMapped, setAutoMapped] = useState(true)

  // Step 1 state
  const [animals, setAnimals] = useState([]) // enriched with match info

  // Step 2 state
  const [importResult, setImportResult] = useState(null)

  // Load cattle for RFID matching
  const { data: cattleData } = useQuery({
    queryKey: ['cattle', ranchId, 'all-for-match'],
    queryFn: () => getAllCattle(ranchId, { status: '' }),
    staleTime: 60000,
  })

  const cattleByRfid = useMemo(() => {
    const map = {}
    const items = cattleData || []
    for (const a of items) {
      if (a.rfid_tag) map[a.rfid_tag] = a
    }
    return map
  }, [cattleData])

  // Import mutation
  const importMutation = useMutation({
    mutationFn: (data) => api.post('/dairy/milk-sessions', data).then(res => res.data),
    onSuccess: (data) => {
      setImportResult(data)
      setStep(2)
    },
    onError: (err) => {
      setError(err.response?.data?.detail || 'Import failed')
    }
  })

  // --- Step 0: Upload CSV ---
  const handleFileUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
    setError('')
    setFileName(file.name)

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        if (result.errors.length > 0) {
          setParseErrors(result.errors.slice(0, 5))
        }
        if (result.data.length === 0) {
          setError('No data rows found in CSV')
          return
        }

        const csvHeaders = Object.keys(result.data[0])
        setRawHeaders(csvHeaders)

        const resolved = resolveHeaders(csvHeaders)

        if (!resolved.rfid) {
          setAutoMapped(false)
          // Initialize empty mapping for manual override
          const mapping = {}
          csvHeaders.forEach(h => { mapping[h] = '' })
          setHeaderMapping(mapping)
          setError('Could not auto-detect RFID column. Please map columns manually below.')
        } else if (!resolved.volume) {
          setAutoMapped(false)
          const mapping = {}
          csvHeaders.forEach(h => { mapping[h] = '' })
          // Pre-set the RFID mapping
          mapping[resolved.rfid] = 'rfid'
          setHeaderMapping(mapping)
          setError('Could not auto-detect Volume column. Please map columns manually below.')
        } else {
          setAutoMapped(true)
          // Build mapping from resolved
          const mapping = {}
          csvHeaders.forEach(h => { mapping[h] = '' })
          if (resolved.rfid) mapping[resolved.rfid] = 'rfid'
          if (resolved.volume) mapping[resolved.volume] = 'volume'
          if (resolved.duration) mapping[resolved.duration] = 'duration'
          if (resolved.order) mapping[resolved.order] = 'order'
          setHeaderMapping(mapping)
        }

        // Parse rows using resolved headers (or store raw for manual mapping)
        const rows = result.data.map(row => {
          const rfidCol = resolved.rfid
          const volumeCol = resolved.volume
          const durationCol = resolved.duration
          const orderCol = resolved.order

          return {
            _raw: row,
            rfid_tag: rfidCol ? (row[rfidCol]?.trim() || '') : '',
            volume_liters: volumeCol ? (parseFloat(row[volumeCol]) || 0) : 0,
            duration_minutes: durationCol ? (parseFloat(row[durationCol]) || null) : null,
            milking_order: orderCol ? (parseInt(row[orderCol]) || null) : null,
          }
        }).filter(r => r.rfid_tag || r.volume_liters > 0)

        setParsedRows(result.data) // store raw for re-mapping
        if (resolved.rfid && resolved.volume) {
          setAnimals(rows) // pre-parsed
        }

        if (rows.length === 0 && resolved.rfid) {
          setError('No valid rows found with RFID and volume data')
        }

        setParseErrors(result.errors.slice(0, 5))
      },
      error: (err) => {
        setError('Failed to parse CSV: ' + err.message)
      }
    })
  }

  // Re-map rows when manual mapping changes
  const applyManualMapping = () => {
    const rfidCol = Object.keys(headerMapping).find(k => headerMapping[k] === 'rfid')
    const volumeCol = Object.keys(headerMapping).find(k => headerMapping[k] === 'volume')
    const durationCol = Object.keys(headerMapping).find(k => headerMapping[k] === 'duration')
    const orderCol = Object.keys(headerMapping).find(k => headerMapping[k] === 'order')

    if (!rfidCol) {
      setError('Please map at least the RFID column')
      return
    }
    if (!volumeCol) {
      setError('Please map at least the Volume column')
      return
    }

    const rows = parsedRows.map(row => ({
      _raw: row,
      rfid_tag: rfidCol ? (row[rfidCol]?.trim() || '') : '',
      volume_liters: volumeCol ? (parseFloat(row[volumeCol]) || 0) : 0,
      duration_minutes: durationCol ? (parseFloat(row[durationCol]) || null) : null,
      milking_order: orderCol ? (parseInt(row[orderCol]) || null) : null,
    })).filter(r => r.rfid_tag)

    if (rows.length === 0) {
      setError('No valid rows found with the selected column mapping')
      return
    }

    setAnimals(rows)
    setAutoMapped(true)
    setError('')
  }

  const goToPreview = () => {
    if (!autoMapped) {
      applyManualMapping()
      if (!autoMapped) return
    }
    if (animals.length === 0) {
      setError('Please upload a CSV file first')
      return
    }
    // Enrich rows with cattle match data
    const enriched = animals.map(row => {
      const match = cattleByRfid[row.rfid_tag]
      return {
        ...row,
        matched: !!match,
        animal_id: match?.id || null,
        animal_name: match?.local_name || null,
        animal_category: match?.category || null,
        corrected_rfid: '', // for manual correction
      }
    })
    setAnimals(enriched)
    setStep(1)
    setError('')
  }

  // --- Step 1: Preview & Match ---
  const matchedCount = animals.filter(a => a.matched).length
  const unmatchedCount = animals.filter(a => !a.matched).length
  const totalLiters = animals.filter(a => a.matched).reduce((sum, a) => sum + (a.volume_liters || 0), 0)

  const updateCorrectedRfid = (index, newRfid) => {
    setAnimals(prev => prev.map((a, i) => {
      if (i !== index) return a
      const trimmed = newRfid.trim()
      const match = cattleByRfid[trimmed]
      return {
        ...a,
        corrected_rfid: newRfid,
        rfid_tag: trimmed || a.rfid_tag,
        matched: !!match,
        animal_id: match?.id || null,
        animal_name: match?.local_name || null,
        animal_category: match?.category || null,
      }
    }))
  }

  const handleSubmit = () => {
    const matched = animals.filter(a => a.matched)
    if (matched.length === 0) {
      setError('No matched animals to import')
      return
    }
    setError('')

    const payload = {
      ranch_id: ranchId,
      session_date: sessionDate,
      session_time: sessionTime,
      milking_method: milkingMethod,
      file_name: fileName,
      imported_by: userId,
      animals: matched.map(a => ({
        animal_id: a.animal_id,
        rfid_tag: a.rfid_tag,
        volume_liters: a.volume_liters,
        duration_minutes: a.duration_minutes,
        milking_order: a.milking_order,
      })),
    }

    importMutation.mutate(payload)
  }

  // --- Render ---
  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => step === 0 ? navigate('/dairy') : setStep(step - 1)}
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Import Milk Session</h1>
          <p className="text-gray-600">Upload CSV to record milk volumes for multiple cows</p>
        </div>
      </div>

      <StepIndicator currentStep={step} />

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* ================================================================= */}
      {/* Step 0: Upload */}
      {/* ================================================================= */}
      {step === 0 && (
        <div className="bg-white rounded-lg shadow p-6 max-w-2xl mx-auto">
          <div className="space-y-6">
            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">CSV File</label>
              <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors">
                <Upload className="h-10 w-10 text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">
                  {fileName ? fileName : 'Click to upload milk recording CSV'}
                </span>
                <span className="text-xs text-gray-400 mt-1">Expected: RFID, Volume, Duration, Order</span>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            </div>

            {/* Parse summary */}
            {animals.length > 0 && autoMapped && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-green-700 font-medium">
                  <CheckCircle className="h-5 w-5" />
                  {animals.length} records parsed from CSV
                </div>
                {parseErrors.length > 0 && (
                  <div className="mt-2 text-sm text-amber-700">
                    {parseErrors.length} parse warning(s) -- check your CSV format
                  </div>
                )}
              </div>
            )}

            {/* Column mapping (when auto-match failed) */}
            {!autoMapped && rawHeaders.length > 0 && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-4">
                <div className="flex items-center gap-2 text-amber-700 font-medium">
                  <AlertTriangle className="h-5 w-5" />
                  Manual Column Mapping Required
                </div>
                <div className="space-y-2">
                  {rawHeaders.map(header => (
                    <div key={header} className="flex items-center gap-3">
                      <span className="w-40 text-sm font-mono text-gray-700 truncate" title={header}>{header}</span>
                      <select
                        value={headerMapping[header] || ''}
                        onChange={(e) => setHeaderMapping(prev => ({ ...prev, [header]: e.target.value }))}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                      >
                        {COLUMN_ROLES.map(role => (
                          <option key={role.value} value={role.value}>{role.label}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
                <button
                  onClick={applyManualMapping}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium"
                >
                  Apply Mapping
                </button>
              </div>
            )}

            {/* Session Details */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Session Date</label>
                  <input
                    type="date"
                    value={sessionDate}
                    onChange={(e) => setSessionDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Milking Method</label>
                  <select
                    value={milkingMethod}
                    onChange={(e) => setMilkingMethod(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="hand">Hand Milking</option>
                    <option value="machine">Machine Milking</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Session Time</label>
                <SessionTimeToggle value={sessionTime} onChange={setSessionTime} />
              </div>
            </div>

            <button
              onClick={goToPreview}
              disabled={animals.length === 0 || !autoMapped}
              className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
            >
              Preview & Match
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* Step 1: Preview & Match */}
      {/* ================================================================= */}
      {step === 1 && (
        <div className="space-y-6">
          {/* Summary cards */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Rows</div>
              <div className="text-2xl font-bold">{animals.length}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Matched</div>
              <div className="text-2xl font-bold text-emerald-600">{matchedCount}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Unmatched</div>
              <div className="text-2xl font-bold text-amber-600">{unmatchedCount}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Liters</div>
              <div className="text-2xl font-bold text-blue-600">{totalLiters.toFixed(1)} L</div>
            </div>
          </div>

          {/* Animal Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">RFID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Animal</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Volume (L)</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Duration (min)</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Order</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Correction</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {animals.map((a, i) => (
                    <tr key={i} className={!a.matched ? 'bg-amber-50' : ''}>
                      <td className="px-4 py-3 text-sm font-mono text-gray-900">{a.rfid_tag}</td>
                      <td className="px-4 py-3 text-sm">
                        {a.matched ? (
                          <div>
                            <span className="font-medium text-gray-900">{a.animal_name || a.rfid_tag}</span>
                            {a.animal_category && (
                              <span className="ml-2 text-xs text-gray-500">({a.animal_category})</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-amber-600 flex items-center gap-1">
                            <AlertTriangle className="h-4 w-4" />
                            No match
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium">{a.volume_liters}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-600">{a.duration_minutes ?? '-'}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-600">{a.milking_order ?? '-'}</td>
                      <td className="px-4 py-3">
                        {a.matched ? (
                          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700">Matched</span>
                        ) : (
                          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">Unmatched</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {!a.matched && (
                          <input
                            type="text"
                            value={a.corrected_rfid || ''}
                            onChange={(e) => updateCorrectedRfid(i, e.target.value)}
                            placeholder="Correct RFID"
                            className="w-36 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500"
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between">
            <button
              onClick={() => setStep(0)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <ChevronLeft className="h-5 w-5" />
              Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={importMutation.isPending || matchedCount === 0}
              className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {importMutation.isPending ? (
                <>
                  <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  Importing...
                </>
              ) : (
                <>
                  Import {matchedCount} Records ({totalLiters.toFixed(1)} L)
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* Step 2: Results */}
      {/* ================================================================= */}
      {step === 2 && importResult && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Processed</div>
              <div className="text-2xl font-bold">{importResult.total_cows ?? importResult.total_animals ?? 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Matched</div>
              <div className="text-2xl font-bold text-emerald-600">{importResult.matched ?? importResult.matched_animals ?? 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Unmatched</div>
              <div className="text-2xl font-bold text-amber-600">{importResult.unmatched ?? importResult.unmatched_animals ?? 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Liters</div>
              <div className="text-2xl font-bold text-blue-600">
                {(importResult.total_liters ?? 0).toFixed(1)} L
              </div>
            </div>
          </div>

          {importResult.unmatched > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700">
              <AlertTriangle className="inline h-5 w-5 mr-2" />
              {importResult.unmatched} record(s) could not be matched to an RFID in the system and were skipped.
            </div>
          )}

          {/* Results Table */}
          {importResult.results && importResult.results.length > 0 && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">RFID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Animal</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Volume (L)</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {importResult.results.map((r, i) => (
                      <tr key={i} className={
                        r.status === 'unmatched' ? 'bg-amber-50' :
                        r.status === 'error' ? 'bg-red-50' : ''
                      }>
                        <td className="px-4 py-3">
                          {r.status === 'recorded' && <CheckCircle className="h-5 w-5 text-emerald-500" />}
                          {r.status === 'unmatched' && <AlertTriangle className="h-5 w-5 text-amber-500" />}
                          {r.status === 'error' && <XCircle className="h-5 w-5 text-red-500" />}
                        </td>
                        <td className="px-4 py-3 text-sm font-mono">{r.rfid_tag}</td>
                        <td className="px-4 py-3 text-sm font-medium">{r.animal_name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-right">{r.volume_liters != null ? `${r.volume_liters} L` : '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{r.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-center">
            <button
              onClick={() => navigate('/dairy')}
              className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
            >
              <ArrowLeft className="h-5 w-5" />
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
